import React from 'react';
import { connect } from 'react-redux';
import { Button, Col, Form, Modal, Row, Nav, Tab, InputGroup, Badge } from 'react-bootstrap-5';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBadgeDollar, faBuilding, faBuildingMemo, faDollar, faPercent } from '@fortawesome/pro-solid-svg-icons';

import { setFilterAttribute } from 'bundles/connect/components/store/actions/criteria';

import PropertyTypeInput from './inputs/property_type_input';
import BuildingClassInput from './inputs/building_class_input';
import MultiSelectInput from './inputs/multi_select_input';
import OnMarketDurationToggle from './inputs/on_market_duration_toggle';
import OpportunityZoneToggleInput from './inputs/opportunity_zone_toggle_input';
import RangeInput from './inputs/range_input';
import RegionInput from './inputs/region_input';
import Toggle from './inputs/toggle';

import Filter from './filter';
import { INVESTMENT_TYPE_MAP } from '../../../../constants';
import type { Criteria } from 'bundles/connect/types';
import { getNumActiveFiltersByGroup } from './helpers';
import {
  EL_BUILDING_INFO_FILTER_INDEX,
  INVESTMENT_DETAILS_FILTER_INDEX,
  PROPERTY_INFO_FILTER_INDEX
} from './constants';

type ConnectFiltersModalProps = {
  criteria: Criteria;
  setFilterAttribute: (attribute: string, value: any, options?: { int?: boolean; percent?: boolean }) => void;
  show: boolean;
  onHide: () => void;
}

function ConnectFiltersModal({
  criteria,
  setFilterAttribute,
  show,
  onHide
}: ConnectFiltersModalProps) {
  const filterLengthsByGroup = getNumActiveFiltersByGroup(criteria, true);

  return (
    <Modal
      centered
      className="search-filters"
      fullscreen="sm-down"
      show={show}
      size="lg"
      onHide={onHide}
    >
      <Modal.Header className="flex-row" closeButton>
        <Modal.Title as="h5">Filters</Modal.Title>
      </Modal.Header>
      <Modal.Body className="bg-white p-0">
        <Tab.Container defaultActiveKey="property-information">
          <Row className="g-4 p-4" style={{ height: '70vh', maxHeight: '1000px' }}>
            <Col className="d-flex flex-column bg-md-transparent" md="auto" sm={12} style={{ minWidth: '250px' }}>
              <Nav className="flex-md-column flex-nowrap overflow-x-auto" variant="pills">
                <Nav.Item className="text-nowrap">
                  <Nav.Link
                    className="text-truncate text-start d-flex align-items-center"
                    eventKey="property-information"
                  >
                    <FontAwesomeIcon className="me-2" icon={faBuildingMemo} />
                    Property Information
                    {filterLengthsByGroup[PROPERTY_INFO_FILTER_INDEX] > 0 && (
                      <Badge bg="" className="filter-count-pill ms-md-auto ms-2 fs-x-small" pill>
                        {filterLengthsByGroup[PROPERTY_INFO_FILTER_INDEX]}
                      </Badge>
                    )}
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item className="text-nowrap">
                  <Nav.Link
                    className="text-truncate text-start d-flex align-items-center"
                    eventKey="investment-details"
                  >
                    <FontAwesomeIcon className="me-2" icon={faBadgeDollar} />
                    Investment Details
                    {filterLengthsByGroup[INVESTMENT_DETAILS_FILTER_INDEX] > 0 && (
                      <Badge bg="" className="filter-count-pill ms-md-auto ms-2 fs-x-small" pill>
                        {filterLengthsByGroup[INVESTMENT_DETAILS_FILTER_INDEX]}
                      </Badge>
                    )}
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item className="text-nowrap">
                  <Nav.Link
                    className="text-truncate text-start d-flex align-items-center"
                    eventKey="building-details"
                  >
                    <FontAwesomeIcon className="me-2" icon={faBuilding} />
                    Building Details
                    {filterLengthsByGroup[EL_BUILDING_INFO_FILTER_INDEX] > 0 && (
                      <Badge bg="" className="filter-count-pill ms-md-auto ms-2 fs-x-small" pill>
                        {filterLengthsByGroup[EL_BUILDING_INFO_FILTER_INDEX]}
                      </Badge>
                    )}
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            </Col>
            <Col className="d-flex flex-column h-100" md sm={12}>
              <Tab.Content className="flex-grow-1 overflow-auto">
                <Tab.Pane className="p-0" eventKey="property-information">
                  <div className="d-flex flex-column gap-3">
                    <Filter className="border rounded overflow-hidden" title="Property Type">
                      <PropertyTypeInput />
                    </Filter>
                    <Filter title="Price">
                      <RangeInput
                        addOn={<FontAwesomeIcon icon={faDollar} />}
                        maxAttribute="maxSalePrice"
                        minAttribute="minSalePrice"
                        placeholder=""
                      />
                    </Filter>
                    <Filter>
                      <Toggle
                        checked={!!criteria.recentPriceReduction}
                        label="Recent Price Reduction"
                        onClick={() => setFilterAttribute('recentPriceReduction', !criteria.recentPriceReduction)}
                      />
                    </Filter>
                    <Filter title="Cap Rate">
                      <RangeInput
                        addOn={<FontAwesomeIcon icon={faPercent} />}
                        maxAttribute="maxCapRate"
                        minAttribute="minCapRate"
                        percent={true}
                        placeholder=""
                      />
                    </Filter>
                    <Filter title="Region">
                      <RegionInput />
                    </Filter>
                  </div>
                </Tab.Pane>
                <Tab.Pane className="p-0" eventKey="investment-details">
                  <div className="d-flex flex-column gap-3">
                    <Filter bordered title="Investment Type">
                      <MultiSelectInput criteriaKey="investmentTypeIds" options={INVESTMENT_TYPE_MAP} />
                    </Filter>
                    <Filter title="Property Use">
                      <Form.Select
                        className={classNames('as-button', criteria.propertyUse ? 'active' : 'text-muted')}
                        value={criteria.propertyUse}
                        onChange={e => setFilterAttribute('propertyUse', e.target.value, { int: true })}
                      >
                        <option value={''}>Any</option>
                        <option value={1}>Net Lease Investment (NNN)</option>
                        <option value={2}>Investment</option>
                        <option value={3}>Owner / User</option>
                        <option value={4}>Business for Sale</option>
                        <option value={5}>Development</option>
                      </Form.Select>
                    </Filter>
                    <Filter title="Tenancy">
                      <Form.Select
                        className={classNames('as-button', criteria.tenancyId ? 'active' : 'text-muted')}
                        value={criteria.tenancyId}
                        onChange={e => setFilterAttribute('tenancyId', e.target.value, { int: true })}
                      >
                        <option value={''}>Any</option>
                        <option value={1}>Single</option>
                        <option value={2}>Multiple</option>
                      </Form.Select>
                    </Filter>
                    <Filter>
                      <OpportunityZoneToggleInput />
                    </Filter>
                    <Filter>
                      <OnMarketDurationToggle />
                    </Filter>
                    <Filter>
                      <Toggle
                        checked={!!criteria.includeUnderContract}
                        label="Include Under Contract"
                        onClick={() => setFilterAttribute('includeUnderContract', !criteria.includeUnderContract)}
                      />
                    </Filter>
                  </div>
                </Tab.Pane>
                <Tab.Pane className="p-0" eventKey="building-details">
                  <div className="d-flex flex-column gap-3">
                    <Filter title="Building SF">
                      <RangeInput
                        maxAttribute="maxBuildingSizeSf"
                        minAttribute="minBuildingSizeSf"
                        placeholder=""
                      />
                    </Filter>
                    <Filter title="Number of Units">
                      <RangeInput
                        maxAttribute="maxNumberOfUnits"
                        minAttribute="minNumberOfUnits"
                        placeholder=""
                      />
                    </Filter>
                    <Filter title="Year Built">
                      <RangeInput
                        maxAttribute="maxYearBuilt"
                        minAttribute="minYearBuilt"
                        placeholder=""
                      />
                    </Filter>
                    <Filter title="Lot Size (Acres)">
                      <RangeInput
                        maxAttribute="maxLotSizeAcres"
                        minAttribute="minLotSizeAcres"
                        placeholder=""
                      />
                    </Filter>
                    <Filter title="Dock High Doors">
                      <RangeInput
                        maxAttribute="maxDockHighDoors"
                        minAttribute="minDockHighDoors"
                        placeholder=""
                      />
                    </Filter>
                    <Filter bordered title="Building Class">
                      <BuildingClassInput />
                    </Filter>
                    <Filter title="Max Vacancy">
                      <InputGroup>
                        <InputGroup.Text>
                          <FontAwesomeIcon icon={faPercent} />
                        </InputGroup.Text>
                        <Form.Control
                          className={criteria.maxVacancyPct ? 'active' : ''}
                          placeholder=""
                          step="0.1"
                          type="number"
                          value={criteria.maxVacancyPct || ''}
                          onChange={e => setFilterAttribute('maxVacancyPct', e.target.value, { percent: true })}
                        />
                      </InputGroup>
                    </Filter>
                  </div>
                </Tab.Pane>
              </Tab.Content>
            </Col>
          </Row>
        </Tab.Container>
      </Modal.Body>
      <Modal.Footer className="p-2">
        <Button variant="text-primary" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}

const mapStateToProps = ({ criteria }) => ({ criteria });

export default connect(mapStateToProps, { setFilterAttribute })(ConnectFiltersModal);
